import './communicationsPage.css';
import React, { FormEvent, useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { useApiData } from '../../../../contexts/apiService';
import { config } from '../../../../../config';
import Dropdown from '../../../../components/common/dropdown/dropdown';
import Checkbox from '../../../../components/common/checkbox/checkbox';
import TextInput from '../../../../components/common/textInput/textInput';
import MobileSplashScreen from './mobileSplashScreen';

const CommunicationsPage: React.FC = () => {
    const { allUsers, fetchAllUsers } = useApiData();

    const [uid, setUid] = useState('');
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [message, setMessage] = useState('');
    const [splashTitle, setSplashTitle] = useState('');
    const [splashBody, setSplashBody] = useState('');

    type CommunicationType = '0' | '1';
    const [communicationType, setCommunicationType] = useState<CommunicationType>('0');

    type RecipientType = 'Myself' | 'Specific user' | 'All users';
    const [activeRecipient, setActiveRecipient] = useState<RecipientType>('Myself');

    const pageOptions = [
        { label: 'Push Notifications', value: '0' },
        { label: 'Splash Screens', value: '1' },
    ];

    const userOptions = allUsers.map((user) => ({
        label: `${user.name || user.email} (${user.email})`,
        value: user.uid,
    }));

    useEffect(() => {
        if (!allUsers.length) {
            fetchAllUsers();
        }
    }, [allUsers.length, fetchAllUsers]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setMessage('');

        const currentUser = getAuth().currentUser;

        if (!currentUser) {
            setMessage('You must be signed in to send notifications.');
            return;
        }

        if (
            activeRecipient === 'Specific user' &&
            !uid
        ) {
            setMessage('Select a user to notify.');
            return;
        }

        if (!title.trim() || !body.trim()) {
            setMessage('Enter both a title and message.');
            return;
        }

        const targetUid =
            activeRecipient === 'Myself'
                ? currentUser.uid
                : uid;

        setIsSending(true);

        try {
            const token = await currentUser.getIdToken();

            const response = await fetch(config.apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    'Request-Platform': 'web',
                    'X-Request-Type': 'send_push_notification',
                },
                body: JSON.stringify({
                    uid: targetUid,
                    title: title.trim(),
                    body: body.trim(),
                }),
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.error || 'Unable to send notification.');
            }

            setMessage(
                `Notification sent to ${result.sent} device(s). Failed: ${result.failed}.`
            );
            setTitle('');
            setBody('');
        } catch (error) {
            setMessage(
                error instanceof Error
                    ? error.message
                    : 'Unable to send notification.'
            );
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="communications-mainContainer">

            <div className="communications-dropdownContainer">
                <Dropdown
                    value={communicationType}
                    onChange={(selectedValue) => setCommunicationType(selectedValue as CommunicationType)}
                    options={pageOptions}
                    className="dropdown-inputMatch"
                />
            </div>

            {communicationType === '0' && (
                <div>
                    <div className="communications-checkboxContainer">
                        <label style={{ color: 'var(--color-black)' }}>
                            Notification Recipient
                        </label>
                        <Checkbox label="Myself" checked={activeRecipient === 'Myself'} ghostUnchecked={true}
                            onChange={(checked) => {if (checked) setActiveRecipient('Myself')}}
                         />
                        <Checkbox label="Specific user" checked={activeRecipient === 'Specific user'} ghostUnchecked={true}
                            onChange={(checked) => {if (checked) setActiveRecipient('Specific user')}}
                        />
                        {activeRecipient === 'Specific user' && (
                            <Dropdown
                                value={uid}
                                onChange={(selectedValue) => setUid(selectedValue)}
                                options={userOptions}
                                className="dropdown-inputMatch"
                            />
                        )}
                        {/* <Checkbox label="All users" checked={activeRecipient === 'All users'} ghostUnchecked={true}
                            onChange={(checked) => {if (checked) setActiveRecipient('All users')}} 
                        /> */}
                    </div>

                <form onSubmit={handleSubmit} className="communications-formContainer">
                    <label>
                        <TextInput
                            value={title}
                            onChange={setTitle}
                            placeholder="Notification title"
                            width={400}
                            height={30}
                        />
                    </label>

                    <label>
                        <textarea
                            value={body}
                            onChange={(event) => setBody(event.target.value)}
                            placeholder="Notification message"
                            rows={5}
                            maxLength={500}
                            required
                        />
                    </label>
                    <div className="communications-buttonContainer">
                        <button type="submit" disabled={isSending}> 
                            {isSending ? 'Sending...' : 'Send Notification'}
                        </button>
                    </div>
                </form>
                {message && <p role="status">{message}</p>}
                </div>
            )}
            {communicationType === '1' && (
                <div className="communications-splashContainer">
                    <div className="communications-splashForm">
                        <label>
                            Splash screen title
                            <TextInput
                                value={splashTitle}
                                onChange={setSplashTitle}
                                placeholder="Splash screen title"
                                width={400}
                                height={30}
                            />
                        </label>

                        <label>
                            Splash screen body
                            <textarea
                                value={splashBody}
                                onChange={(event) => setSplashBody(event.target.value)}
                                placeholder="Splash screen message"
                                rows={5}
                                maxLength={500}
                            />
                        </label>
                    </div>

                    <div className="communications-splashPreview">
                        <MobileSplashScreen
                            title={splashTitle || 'Splash screen title'}
                            body={splashBody || 'Splash screen message'}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CommunicationsPage;